import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Plus,
  Search,
  FileText,
  Clock,
  Edit3,
  RefreshCw,
  User,
  CheckCircle,
} from 'lucide-react';
import { clinicalNotesApi } from '@/api/endpoints/clinical.api';
import { NoteStatus, NoteTypeLabels, NoteStatusLabels } from '@/types/clinical.types';

export default function ClinicalNotesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || 'all');
  const [typeFilter, setTypeFilter] = useState<string>(searchParams.get('type') || 'all');
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // Debounce search
  useState(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  });

  // Main query with React Query
  const {
    data: notesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['clinical-notes', { page, size: pageSize, search: debouncedSearch, status: statusFilter, type: typeFilter }],
    queryFn: () => clinicalNotesApi.getAll({
      page,
      size: pageSize,
      search: debouncedSearch || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      type: typeFilter !== 'all' ? typeFilter : undefined,
    }),
    staleTime: 1000 * 60 * 2, // 2 minutes
    placeholderData: keepPreviousData,
  });

  const notes = notesData?.content || [];
  const totalElements = notesData?.totalElements || 0;
  const totalPages = notesData?.totalPages || 0;

  const getStatusIcon = (status: NoteStatus) => {
    switch (status) {
      case NoteStatus.DRAFT:
        return <Edit3 className="h-4 w-4 text-yellow-500" />;
      case NoteStatus.SIGNED:
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case NoteStatus.AMENDED:
        return <FileText className="h-4 w-4 text-blue-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadgeColor = (status: NoteStatus) => {
    switch (status) {
      case NoteStatus.DRAFT:
        return 'bg-yellow-100 text-yellow-800';
      case NoteStatus.SIGNED:
        return 'bg-green-100 text-green-800';
      case NoteStatus.AMENDED:
        return 'bg-blue-100 text-blue-800';
      case NoteStatus.CANCELLED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Evoluções Clínicas</h1>
          <p className="text-muted-foreground">
            Gerencie evoluções SOAP e registros clínicos
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 rounded-lg border px-4 py-2 hover:bg-muted"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          <button
            onClick={() => navigate('/clinical/new')}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Nova Evolução
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por paciente, profissional ou conteúdo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border bg-background py-2 pl-10 pr-4"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            className="rounded-lg border bg-background px-3 py-2"
          >
            <option value="all">Todos os Status</option>
            {Object.entries(NoteStatusLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
            className="rounded-lg border bg-background px-3 py-2"
          >
            <option value="all">Todos os Tipos</option>
            {Object.entries(NoteTypeLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-800">Erro ao carregar notas clínicas</p>
        </div>
      )}

      {/* Loading State */}
      {isLoading && notes.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : (
        <>
          {/* Notes List */}
          {notes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">Nenhuma evolução encontrada</h3>
              <p className="mt-2 text-muted-foreground">
                {search || statusFilter !== 'all' || typeFilter !== 'all'
                  ? 'Tente ajustar os filtros de busca'
                  : 'Clique em "Nova Evolução" para começar'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <div
                  key={note.id}
                  onClick={() => navigate(`/clinical/${note.id}`)}
                  className="cursor-pointer rounded-lg border bg-card p-4 transition hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      {getStatusIcon(note.status)}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{note.patientName}</h3>
                          <span className={`rounded-full px-2 py-0.5 text-xs ${getStatusBadgeColor(note.status)}`}>
                            {NoteStatusLabels[note.status] || note.statusLabel || note.status}
                          </span>
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                            {NoteTypeLabels[note.type] || note.typeLabel || note.type}
                          </span>
                        </div>
                        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                          {note.summary || note.subjectiveSummary || note.assessmentSummary || 'Sem resumo disponível'}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {format(new Date(note.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {note.createdByName}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Mostrando {notes.length} de {totalElements} registros
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="rounded-lg border px-3 py-1 disabled:opacity-50"
                >
                  Anterior
                </button>
                <span className="px-3 py-1">
                  {page + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="rounded-lg border px-3 py-1 disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
